export const frontmatter = {
  title: "Лесные данные: откуда полигоны",
  slug: "forest-data",
  category: "Источники данных",
  reading_minutes: 4,
  updated: "2026-04-29",
  abstract: "~2 млн выделов Рослесхоза в карте: как попадают, какие ограничения у источника.",
};

import { Callout, KeyFacts, Stats, Steps, TLDR } from "../../components/mdx";

# Лесные данные: откуда полигоны

<TLDR>
Карта показывает **2,17 млн** лесных выделов всей Ленобласти. Источник — **ФГИС ЛК** Рослесхоза, обновляется раз в 10–15 лет при лесоустройстве. Для каждого выдела знаем породу, бонитет, возраст, запас.
</TLDR>

<Stats stats={[
  { number: "2,17M", label: "полигонов", hint: "вся ЛО" },
  { number: "ФГИС ЛК", label: "источник", hint: "Рослесхоз" },
  { number: "10–15 лет", label: "обновление", hint: "лесоустройство" },
  { number: "12 zoom", label: "детализация", hint: "MVT-тайлы" },
]} />

<KeyFacts title="Источник" facts={[
  { label: "Система", value: "ФГИС ЛК (Рослесхоз)" },
  { label: "Формат", value: "MVT-тайлы, zoom 12" },
  { label: "Проекция", value: "EPSG:3857 → 4326 при загрузке" },
  { label: "Атрибуты", value: "порода, бонитет, возраст, запас, состав смешения" },
  { label: "Лицензия", value: "открытые данные Рослесхоза" },
  { label: "Обновлено в БД", value: "2026-04-16" },
]} />

## Что внутри выдела

Выдел — хозяйственная единица лесничества. Для каждого Рослесхоз публикует:

- **Доминирующую породу** (сосна / ель / берёза / осина / ольха / дуб / смешанные)
- **Бонитет** (I–V, продуктивность; I = лучший)
- **Возрастную группу** (молодняки / средневозрастные / приспевающие / спелые / перестойные)
- **Запас древесины** (м³/га)
- **Формулу смешения** — мы разматываем 3 главные компоненты в JSONB

Юридически эти данные — основной документ лесничеств: по ним ведут учёт рубок, отводят делянки, планируют посадки. Границы выделов реальные, не рисованные «на глаз».

## Пайплайн

<Steps steps={[
  {
    title: "Скачка тайлов",
    body: <>Все MVT-тайлы покрытия ЛО на 12-м зуме в собственной проекции ФГИСа.</>,
  },
  {
    title: "Распаковка в GeoJSON",
    body: <>Через <code>fgislk_tiles_to_geojson.py</code>, сборка одного GeoJSON по bbox области.</>,
  },
  {
    title: "Репроекция в EPSG:4326",
    body: "Требование PostGIS.",
  },
  {
    title: "Нормализация атрибутов",
    body: <>Коды Рослесхоза («С»/«Е»/«Б») → slug'и (<code>pine</code>/<code>spruce</code>/<code>birch</code>). Полный состав → JSONB.</>,
  },
  {
    title: "UPSERT по (source, source_feature_id)",
    body: "Идемпотентно. WKB pass-through через COPY — миллион полигонов не лезет в один INSERT без утечки памяти.",
  },
]} />

После загрузки наполняется view `forest_unified` — он склеивает данные из нескольких источников по приоритету (Рослесхоз > Copernicus > OSM). Для ЛО сейчас активен только Рослесхоз.

## Ограничения

<Callout type="warn" title="Возраст деревьев отстаёт">
Лесоустройство раз в 10–15 лет. Для отдельных кварталов последний обход мог быть в 2012-м: молодняки растут, спелый лес стареет, цифра в базе не двигается, пока следующее лесоустройство не пройдёт.
</Callout>

<Callout type="warn" title="Границы выделов ≠ границы леса">
Выдел — хозяйственная единица. Просеки, дороги, ЛЭП, свежие вырубки попадают между выделами — лесной покров там может быть, но полигона на эту территорию нет.
</Callout>

<Callout type="warn" title="Состав пород — только 3 главные">
Рослесхоз пишет до 10 единиц («8С1Е1Б+Ос»), мы храним 3 основные («С/Е/Б»). Минорные примеси теряются.
</Callout>

<Callout type="warn" title="Нет учёта повреждений">
Короед, пожар, ветровал — слой «forest» покажет лес с номинальной породой. Реальное состояние — частично в слоях `felling` и `protective`.
</Callout>

## Что дальше

- Переход с 12-го зума на дробные тайлы — ускорит локальные обновления
- Слой рубок главного пользования за последние 5 лет (`data/rosleshoz/fgislk_tiles/felling/`)
- «Деградированный участок» (годы с пожара, площадь рубок в окрестности) как фича для модели прогноза — Phase 3+
